import { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';
import { executeRecaptcha } from '../services/recaptcha';

const AuthContext = createContext(null);

/**
 * Pull the most useful error message + per-field errors out of an axios
 * error from one of our API endpoints. The backend returns two shapes:
 *
 *   1. Manual reject (AuthController returns a string):
 *        { error: 'Email already registered' }
 *   2. Laravel validation (the global handler in bootstrap/app.php):
 *        { errors: { email: ['The email has already been taken.'] },
 *          message: 'Validation failed' }
 *
 * We prefer (in order):
 *   - data.error                        — the controller's specific reason
 *   - first message in data.errors      — the validation specific reason
 *   - data.message                      — generic ("Validation failed")
 *   - the `fallback` arg                — caller's default
 *
 * fieldErrors gets returned as a flat { field: 'first message' } map so
 * the form can drop them under the right inputs.
 */
function parseApiError(err, fallback) {
  const resp = err?.response?.data;
  const fieldErrorsRaw = resp?.errors;
  const fieldErrors = fieldErrorsRaw
    ? Object.fromEntries(Object.entries(fieldErrorsRaw).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]))
    : null;

  let message;
  if (resp?.error) message = resp.error;
  else if (fieldErrors) message = Object.values(fieldErrors)[0];
  else message = resp?.message || fallback;

  return { message, fieldErrors };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pv_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const recaptcha_token = await executeRecaptcha('login');
      // Normalize email casing client-side so the user's `JANE@X.COM`
      // resolves to the same row as their signup `jane@x.com`. The
      // backend also lowercases, so this is belt-and-suspenders.
      const normalized = (email || '').trim().toLowerCase();
      const { data } = await api.post('/auth/login', { email: normalized, password, recaptcha_token });
      localStorage.setItem('pv_token', data.token);
      localStorage.setItem('pv_user', JSON.stringify(data.user));
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      // Email-verification gate. The backend returns 403 with
      // requires_verification=true when a known-good password belongs to
      // an unverified account, plus a fresh dev_otp in non-prod envs.
      // We surface this as a distinct outcome so the caller can route to
      // /verify-otp with the email + dev_otp pre-loaded.
      const errData = err?.response?.data;
      if (err?.response?.status === 403 && errData?.requires_verification) {
        return {
          success: false,
          requiresVerification: true,
          email: errData.email || email,
          devOtp: errData.dev_otp || null,
          error: errData.message || 'Please verify your email to finish signing in.',
        };
      }
      const { message, fieldErrors } = parseApiError(err, 'Invalid credentials');
      return { success: false, error: message, fieldErrors };
    } finally { setLoading(false); }
  }, []);

  const register = useCallback(async (formData) => {
    setLoading(true);
    try {
      const recaptcha_token = await executeRecaptcha('register');
      const { data } = await api.post('/auth/register', { ...formData, recaptcha_token });
      // OTP-gated flow: the server doesn't issue a token until verifyOtp
      // succeeds. A successful register response carries requires_verification
      // and the email we should pre-fill on the OTP page.
      if (data.requires_verification) {
        return {
          success: true,
          requiresVerification: true,
          email: data.email,
          devOtp: data.dev_otp || null,
        };
      }
      // Legacy path: if a future change starts auto-issuing tokens again,
      // we still handle it gracefully.
      if (data.token) {
        localStorage.setItem('pv_token', data.token);
        localStorage.setItem('pv_user', JSON.stringify(data.user));
        setUser(data.user);
      }
      return { success: true };
    } catch (err) {
      const { message, fieldErrors } = parseApiError(err, 'Registration failed');
      return { success: false, error: message, fieldErrors };
    } finally { setLoading(false); }
  }, []);

  /**
   * Confirm a 6-digit code emailed at signup / first-login. On success,
   * the backend hands back a session token (same shape as login) and the
   * SPA finally enters the authenticated state.
   */
  const verifyOtp = useCallback(async (email, otp) => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { email, otp });
      localStorage.setItem('pv_token', data.token);
      localStorage.setItem('pv_user', JSON.stringify(data.user));
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      const { message, fieldErrors } = parseApiError(err, 'That code didn\'t match.');
      return { success: false, error: message, fieldErrors };
    } finally { setLoading(false); }
  }, []);

  /** Re-issue an OTP for an unverified account. Returns the dev_otp in non-prod. */
  const resendOtp = useCallback(async (email) => {
    try {
      const { data } = await api.post('/auth/resend-otp', { email });
      return { success: true, devOtp: data.dev_otp || null, message: data.message };
    } catch (err) {
      const { message } = parseApiError(err, 'Could not resend the code right now.');
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('pv_token');
    localStorage.removeItem('pv_user');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.data);
      localStorage.setItem('pv_user', JSON.stringify(data.data));
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, verifyOtp, resendOtp, logout, refreshUser,
      isAdmin:    () => user?.role === 'super_admin',
      isMerchant: () => user?.role === 'merchant_admin',
      isLoggedIn: () => !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
